import { useState } from "react";
import { UserPlus, MessageCircle, Link2, Check } from "lucide-react";
import { toast } from "sonner";
import { downloadVcard } from "@/utils/vcard";
import { personUrl, COMPANY } from "@/data/founders";

const pill =
  "flex items-center gap-2 rounded-full border border-gold/40 text-[1.65cqh] font-medium tracking-[0.3em] text-gold transition-colors duration-300 hover:border-gold hover:bg-gold/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60";

const copyText = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const el = document.createElement("textarea");
  el.value = text;
  el.setAttribute("readonly", "");
  el.style.position = "fixed";
  el.style.opacity = "0";
  document.body.appendChild(el);
  el.select();
  document.execCommand("copy");
  document.body.removeChild(el);
};

export const ShareActions = ({ founder }) => {
  const [copied, setCopied] = useState(false);
  const cardUrl = personUrl(founder.slug);
  const shareText = `${founder.name} · ${founder.designation}, ${COMPANY}\n${cardUrl}`;

  const handleSave = (e) => {
    e.stopPropagation();
    downloadVcard(founder);
  };

  const handleWhatsApp = (e) => {
    e.stopPropagation();
    window.open(
      `https://wa.me/?text=${encodeURIComponent(shareText)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await copyText(cardUrl);
      setCopied(true);
      toast.success("Card link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — long-press the address bar instead");
    }
  };

  return (
    <div
      data-testid="share-actions"
      className="mx-auto mt-[2.2cqh] flex items-center gap-[2.5cqw]"
    >
      <button
        type="button"
        data-testid="save-contact-button"
        onClick={handleSave}
        className={`${pill} h-[5.8cqh] px-[5cqw]`}
      >
        <UserPlus className="h-[2.3cqh] w-[2.3cqh]" strokeWidth={1.75} />
        <span className="pl-[0.3em]">SAVE CONTACT</span>
      </button>
      <button
        type="button"
        data-testid="share-whatsapp-button"
        aria-label="Share this card on WhatsApp"
        title="Share on WhatsApp"
        onClick={handleWhatsApp}
        className={`${pill} h-[5.8cqh] w-[5.8cqh] justify-center`}
      >
        <MessageCircle className="h-[2.5cqh] w-[2.5cqh]" strokeWidth={1.75} />
      </button>
      <button
        type="button"
        data-testid="copy-link-button"
        aria-label={copied ? "Link copied" : "Copy card link"}
        title="Copy link"
        onClick={handleCopy}
        className={`${pill} h-[5.8cqh] w-[5.8cqh] justify-center ${copied ? "border-gold bg-gold/15" : ""}`}
      >
        {copied ? (
          <Check className="h-[2.5cqh] w-[2.5cqh]" strokeWidth={2} />
        ) : (
          <Link2 className="h-[2.5cqh] w-[2.5cqh]" strokeWidth={1.75} />
        )}
      </button>
    </div>
  );
};
